import { buildChatBotInstallationSnippets } from "@jugl-web/domain-resources/chat-bot/utils/buildChatBotInstallationSnippets";
import { useTranslations } from "@jugl-web/utils";
import { environment } from "@web-src/environments/environment";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import React, { ReactNode, useMemo } from "react";
import { CRMAccountCard } from "../../../../components/CRMAccountCard";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { ReactComponent as InstallIcon } from "./assets/install.svg";
import { CopyableCodeSnippet } from "./components/CopyableCodeSnippet";

const BOT_JS_URL = environment.chatBotUrl;

const instructionListClassName =
  "m-0 list-disc space-y-1.5 pl-5 font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark-800";

export const InstallTabContent: React.FC = () => {
  const { t } = useTranslations();
  const { webChatBotAccount } = useWebChatBotAccount();

  const token = webChatBotAccount?.token ?? "";

  const { webScript, mobileUrl, mobileCode } = useMemo(
    () =>
      buildChatBotInstallationSnippets({
        token,
        chatbotUrl: BOT_JS_URL,
      }),
    [token]
  );

  return (
    <div className="flex animate-fade-in flex-col gap-8 bg-grey-100 p-8 max-lg:gap-4 max-lg:p-4">
      <CRMAccountCard
        title={t({
          id: "client-support-page.install-messenger-on-your-site",
          defaultMessage: "Install the messenger on your site or app",
        })}
        description={t({
          id: "client-support-page.enable-live-chat-on-your-website",
          defaultMessage:
            "Enable live chat on your website and manage conversations in Jugl CRM",
        })}
        icon={InstallIcon}
      />
      <CRMInfoSection
        title={t({
          id: "client-support-page.how-to-install",
          defaultMessage: "How to install:",
        })}
        subtitle={
          <ul className={instructionListClassName}>
            <li>
              {t(
                {
                  id: "client-support-page.paste-code-snippet-before-tag",
                  defaultMessage: `Paste this code snippet before the {bodyTag} tag on every page you want messenger to appear`,
                },
                { bodyTag: "</body>" }
              )}
            </li>
            <li>
              {t({
                id: "client-support-page.must-be-done-on-client-side",
                defaultMessage: "This must be done in your client-side code ℹ️",
              })}
            </li>
            <li>
              {t({
                id: "client-support-page.reload-webiste-to-appear-web-chat",
                defaultMessage:
                  "Once your done - reload the website and web chat should appear in bottom left or right corner",
              })}
            </li>
            <li>
              {t(
                {
                  id: "client-support-page.chat-is-not-appearing",
                  defaultMessage:
                    "If chat is not appearing - please try again or reach out to <email>support</email>",
                },
                {
                  email: (text: ReactNode) => (
                    <a
                      className="text-[13px] leading-[150%] font-medium tracking-[0.13px] text-primary-800 underline"
                      href="mailto:support@jugl.com"
                    >
                      {text}
                    </a>
                  ),
                }
              )}
            </li>
          </ul>
        }
      >
        <CopyableCodeSnippet value={webScript} />
      </CRMInfoSection>
      <CRMInfoSection
        title={t({
          id: "client-support-page.install-in-wordpress-plugin",
          defaultMessage: "Install in WordPress plugin:",
        })}
        subtitle={
          <ul className={instructionListClassName}>
            <li>
              {t(
                {
                  id: "client-support-page.install-in-wordpress-plugin-step-download-activate",
                  defaultMessage:
                    "Download and activate <link>Jugl Chat Widget</link> in your WordPress account",
                },
                {
                  link: (text: ReactNode) => (
                    <a
                      className="font-medium text-primary-800 underline"
                      href="https://wordpress.org/plugins/jugl-cx-widget/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {text}
                    </a>
                  ),
                }
              )}
            </li>
            <li>
              {t({
                id: "client-support-page.install-in-wordpress-plugin-step-copy-token",
                defaultMessage: "Copy the Jugl token below",
              })}
            </li>
            <li>
              {t({
                id: "client-support-page.install-in-wordpress-plugin-step-go-to-settings",
                defaultMessage: "Go to WordPress Settings → Jugl Chat Widget",
              })}
            </li>
            <li>
              {t({
                id: "client-support-page.install-in-wordpress-plugin-step-paste-token",
                defaultMessage: "Paste the Jugl token",
              })}
            </li>
            <li>
              {t({
                id: "client-support-page.install-in-wordpress-plugin-step-widget-appears",
                defaultMessage:
                  "Chat widget will appear on every public page of your site",
              })}
            </li>
          </ul>
        }
      >
        <CopyableCodeSnippet value={token} />
      </CRMInfoSection>
      <CRMInfoSection
        title={t({
          id: "client-support-page.install-on-mobile",
          defaultMessage: "Install on Mobile:",
        })}
        subtitle={
          <ul className={instructionListClassName}>
            <li>
              {t({
                id: "client-support-page.open-url-in-webview",
                defaultMessage:
                  "Open this URL in your mobile app's WebView to display the chat",
              })}
            </li>
          </ul>
        }
      >
        <div className="flex flex-col gap-4">
          <CopyableCodeSnippet value={mobileUrl} />
          <CopyableCodeSnippet value={mobileCode} />
        </div>
      </CRMInfoSection>
    </div>
  );
};
